/* eslint-disable no-restricted-imports -- this module is the sanctioned wrapper around react-toastify's toast */
import { isValidElement } from 'react';
import { toast as baseToast, ToastContent, ToastOptions } from 'react-toastify';
import { resolveErrorMessage } from './errorMessage';

type ToastId = ReturnType<typeof baseToast>;

/**
 * Coerces a value into something react-toastify is able to render.
 *
 * react-toastify v8 only renders content that passes its internal
 * `canBeRendered` check, and that check accepts nothing but React elements,
 * strings, functions and numbers. Every other value, most importantly the
 * `Error` that `helpers/api` throws and that our `catch` blocks hand straight
 * to `toast.error`, is dropped without a toast and without a warning, so the
 * user sees no feedback at all.
 *
 * @param content - The value a caller passed as toast content.
 * @returns The same value when react-toastify can render it, a readable string otherwise.
 */
export function toRenderableToastContent(content: unknown): ToastContent {
	if (content instanceof Error) {
		return content.message;
	}

	if (typeof content === 'object' && content !== null && !isValidElement(content)) {
		return String(content);
	}

	return content as ToastContent;
}

/**
 * Replaces a caught error with localized copy before anything renders it.
 *
 * An `Error` is never display copy. `helpers/api` puts the backend's English
 * response body in `message` so it reaches Sentry, and the hand-rolled
 * `throw new Error(...)` sites across `src/` carry developer strings, so
 * rendering `message` shows English to every locale. `resolveErrorMessage`
 * turns it into copy from the translation files and passes through the
 * errors a thrower marked as already localized.
 *
 * This sits in front of `toRenderableToastContent` rather than inside it so
 * that function keeps its single job, deciding what react-toastify is able to
 * render at all.
 *
 * @param content - The value a caller passed as toast content.
 * @returns Localized copy when the value was an error, the value otherwise.
 */
export function toDisplayableToastContent(content: unknown): unknown {
	return content instanceof Error ? resolveErrorMessage(content) : content;
}

type BaseNotifier = (content: ToastContent, options?: ToastOptions) => ToastId;
type CoercingNotifier = (content: unknown, ...rest: [options?: ToastOptions]) => ToastId;

/** Names of the `toast` methods whose first argument is toast content. */
type ContentMethod = 'error' | 'success' | 'info' | 'warn' | 'warning' | 'dark' | 'loading';

export type CoercingToast = Omit<typeof baseToast, ContentMethod> &
	Record<ContentMethod, CoercingNotifier> &
	CoercingNotifier;

// `rest` is forwarded instead of always passing `options`, so a one-argument
// call stays a one-argument call on the wrapped notifier.
const coercing =
	(notify: BaseNotifier): CoercingNotifier =>
	(content, ...rest) =>
		notify(toRenderableToastContent(toDisplayableToastContent(content)), ...rest);

/**
 * Drop-in replacement for react-toastify's `toast` that localizes errors and
 * coerces non-renderable content before delegating. Everything react-toastify
 * exposes on `toast` (`dismiss`, `promise`, `update`, `POSITION`, ...) is
 * preserved as-is.
 */
export const toast: CoercingToast = Object.assign(
	(content: unknown, ...rest: [options?: ToastOptions]) =>
		baseToast(toRenderableToastContent(toDisplayableToastContent(content)), ...rest),
	baseToast,
	{
		error: coercing(baseToast.error),
		success: coercing(baseToast.success),
		info: coercing(baseToast.info),
		warn: coercing(baseToast.warn),
		warning: coercing(baseToast.warning),
		dark: coercing(baseToast.dark),
		loading: coercing(baseToast.loading),
	},
) as CoercingToast;
